import {
	ChangeDetectionStrategy,
	Component,
	input,
	InputSignal,
	OnDestroy,
	OnInit,
	output,
	OutputEmitterRef,
	signal,
	WritableSignal,
	inject,
} from '@angular/core'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { TuiIcon } from '@taiga-ui/core'
import { DatePipe, UpperCasePipe } from '@angular/common'
import { OrderSideEnum } from '@modules/orders-module/enums/order-side.enum'
import { Subject, takeUntil } from 'rxjs'
import { ChangedSimpleOrderInterface } from '@modules/orders-module/interfaces/changed-simple-order.interface'
import { SimpleOrderFormInterface } from '@modules/orders-module/interfaces/simple-order-form.interface'
import { HttpErrorResponse } from '@angular/common/http'
import { OptionTypeEnum } from '@modules/options-module/general/enums/option-type.enum'
import { OptionOrderInterface } from '@modules/options-module/orders/interfaces/option-order.interface'
import { OptionsOrdersHelperService } from '@modules/options-module/orders/services/options-orders-helper.service'
import { OptionOrderStateEnum } from '@modules/options-module/orders/enums/option-order-state.enum'
import { OptionsOrdersService } from '@modules/options-module/orders/services/options-orders.service'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { PricePipe } from '@shared/pipes/price.pipe'
import { QuantityPipe } from '@shared/pipes/quantity.pipe'
import { AlertService } from '@shared/services/alert.service'
import { ButtonComponent } from '@shared/components/ui-kit/button/button.component'
import { OrderPriceInputComponent } from '@shared/components/trading/orders/common/order-price-input/order-price-input.component'

@UseLocales({ ru, en })
@Component({
	selector: 'app-change-options-order',
	templateUrl: './change-options-order.component.html',
	styleUrl: './change-options-order.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		HighlightDirective,
		ReactiveFormsModule,
		TranslatePipe,
		TuiIcon,
		UpperCasePipe,
		DatePipe,
		PricePipe,
		QuantityPipe,
		ButtonComponent,
		OrderPriceInputComponent,
	],
})
export class ChangeOptionsOrderComponent implements OnInit, OnDestroy {
	protected readonly orderSideEnum: typeof OrderSideEnum = OrderSideEnum
	protected readonly optionTypeEnum: typeof OptionTypeEnum = OptionTypeEnum
	protected readonly optionOrderStateEnum: typeof OptionOrderStateEnum = OptionOrderStateEnum

	public order: InputSignal<OptionOrderInterface | null> = input<OptionOrderInterface | null>(null)
	public closeDropdown: OutputEmitterRef<void> = output<void>()

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _formBuilder: FormBuilder = inject<FormBuilder>(FormBuilder)
	private readonly _optionsOrderService: OptionsOrdersService = inject<OptionsOrdersService>(OptionsOrdersService)
	private readonly _alertService: AlertService = inject<AlertService>(AlertService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)
	private readonly _optionsOrderHelperService: OptionsOrdersHelperService =
		inject<OptionsOrdersHelperService>(OptionsOrdersHelperService)

	protected isLoading: WritableSignal<boolean> = signal<boolean>(false)

	protected form: FormGroup | null = null
	private _initForm(): void {
		this.form = this._formBuilder.group({
			limit_price: [null],
		})
	}

	private _fillForm(): void {
		if (!this.form) return

		this.form.patchValue({
			limit_price: this.order()?.limit_price,
		})
	}

	private _prepareSimpleOrderToChange(id: string, limit_price: number): ChangedSimpleOrderInterface {
		return {
			id,
			...(limit_price != null ? { limit_price } : {}),
		}
	}

	protected onSubmit(): void {
		const order: OptionOrderInterface | null = this.order()
		if (!order) return

		if (!this.form || this.form.invalid || !order.id) return

		const simpleOrderFormValue: SimpleOrderFormInterface = this.form.value

		const preparedOrder: ChangedSimpleOrderInterface = this._prepareSimpleOrderToChange(
			order.id,
			simpleOrderFormValue.limit_price,
		)

		this.isLoading.set(true)
		this._optionsOrderService
			.changeOptionOrder(preparedOrder)
			.pipe(takeUntil(this._destroy$))
			.subscribe(
				(response: OptionOrderInterface): void => {
					this._optionsOrderHelperService.changeOptionOrder(response, this.optionOrderStateEnum.OPEN)
					this._alertService.success(this._translateService.instant('changeOrder.orderSuccessfullyChanged'))
					this.isLoading.set(false)
					this.onCloseDropdown()
				},
				(error: HttpErrorResponse): void => {
					this._alertService.error(
						error?.error?.error?.message ||
							this._translateService.instant("changeOrder.couldn'tChangeOrder"),
					)
					this.isLoading.set(false)
				},
			)
	}

	protected onCloseDropdown(): void {
		this.closeDropdown.emit()
	}

	ngOnInit(): void {
		this._initForm()
		this._fillForm()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
